import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, FileText, Package, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const estadoBadge = (estado) => {
  const map = {
    'Borrador': 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
    'Enviada': 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
    'Adjudicada': 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    'En espera': 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    'No adjudicada': 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
  };
  return map[estado] || '';
};

const tipoBadge = (tipo) => {
  const map = {
    'CFR': 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
    'CIF': 'bg-purple-500/15 text-purple-700 dark:text-purple-400',
    'FOB': 'bg-teal-500/15 text-teal-700 dark:text-teal-400',
  };
  return map[tipo] || '';
};

const formatEmbarque = (fechaStr) => {
  if (!fechaStr) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) {
    const parts = fechaStr.split('-');
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('es-UY');
  }
  return fechaStr;
};

const Publicaciones = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const limit = 20;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        searchTerm: searchTerm || undefined,
        estadoFilter: estadoFilter !== 'all' ? estadoFilter : undefined,
        tipoFilter: tipoFilter !== 'all' ? tipoFilter : undefined,
        sortField,
        sortDirection,
      };
      const res = await axios.get(`${API}/publicaciones/paginated`, { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error('Error al cargar ofertas');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, estadoFilter, tipoFilter, sortField, sortDirection]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const SortHeader = ({ field, children }) => (
    <TableHead className="cursor-pointer select-none" onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Ofertas</h1>
              <p className="text-muted-foreground mt-1">{total} oferta(s)</p>
            </div>
            <Button onClick={() => navigate('/publicaciones/nueva')} data-testid="new-publication-button">
              <Plus className="h-4 w-4 mr-2" /> Nueva Oferta
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por frigorífico, tipo..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                className="pl-10"
              />
            </div>
            <Select value={estadoFilter} onValueChange={(v) => { setEstadoFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Borrador">Borrador</SelectItem>
                <SelectItem value="Enviada">Enviada</SelectItem>
                <SelectItem value="Adjudicada">Adjudicada</SelectItem>
                <SelectItem value="En espera">En espera</SelectItem>
                <SelectItem value="No adjudicada">No adjudicada</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tipoFilter} onValueChange={(v) => { setTipoFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[120px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="CFR">CFR</SelectItem>
                <SelectItem value="CIF">CIF</SelectItem>
                <SelectItem value="FOB">FOB</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader field="frigorificoNombre">Frigorífico</SortHeader>
                <TableHead>Planta</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>FCLs</TableHead>
                <TableHead>Embarque</TableHead>
                <TableHead>Bids</TableHead>
                <SortHeader field="estadoPublicacion">Estado</SortHeader>
                <SortHeader field="createdAt">Fecha</SortHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Cargando...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No hay ofertas</TableCell></TableRow>
              ) : (
                items.map((pub) => (
                  <TableRow
                    key={pub.id}
                    className="cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => navigate(`/publicaciones/${pub.id}`)}
                  >
                    <TableCell className="font-medium">{pub.frigorificoNombre}</TableCell>
                    <TableCell>#{pub.plantaNumero}</TableCell>
                    <TableCell><Badge className={typoBadge(pub.tipo)}>{pub.tipo}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Package className="h-3.5 w-3.5 text-muted-foreground" />
                        {pub.fcls?.length || 0}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatEmbarque(pub.estimadoEmbarque)}</TableCell>
                    <TableCell>
                      {pub.ofertasCount > 0 ? (
                        <Badge variant="secondary">{pub.ofertasCount}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell><Badge className={estadoBadge(pub.estadoPublicacion)}>{pub.estadoPublicacion}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(pub.createdAt).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between p-4 border-t">
              <span className="text-sm text-muted-foreground">
                {page * limit + 1}–{Math.min((page + 1) * limit, total)} de {total}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Anterior</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}>Siguiente</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// Helper - fix typo in function name for JSX usage
const typoBadge = tipoBadge;

export default Publicaciones;